// Compatibility facade. Every export that lived here still lives here — call sites keep
// importing from 'lib/api'. Transport moved to api/client.ts; the domain modules below
// were extracted verbatim (frontend containment F1).
//
// The body that follows is not yet split and remains the legacy location. Extract from it
// incrementally; do not add new exports here.
import { request, BASE } from './api/client';
import type { GeorgeMessage } from './api/george-messages';

export { request } from './api/client';
export * from './api/strafe';
export * from './api/george-messages';
export * from './api/meta';
export * from './api/super-admin';
export * from './api/bug-report';


// Clients
export const getClients = (activeOnly = false) =>
  request<string[]>(`/clients${activeOnly ? '?active_only=true' : ''}`);
export const getClientContext = (client: string) =>
  request<{ context_md: string; stage: string }>(`/clients/${encodeURIComponent(client)}/context`);
export const getClientContacts = (client: string) =>
  request<string[]>(`/clients/${encodeURIComponent(client)}/contacts`);
export const getInternalContacts = () => request<string[]>('/internal-contacts');

// Dashboard
export const getDashboard = (myDeals = false) =>
  request<{ built_at: string | null; rows: DashboardRow[]; outbound_eval_credits: { used: number; free: number; remaining: number } | null }>(`/dashboard${myDeals ? '?my_deals=true' : ''}`);

// Dashboard view filter state — per-user, per-view, server-backed so it
// syncs across devices/sessions instead of being trapped in localStorage.
export const getDashboardViewStates = () =>
  request<{ view_states: Record<string, { search?: string; stageFilter?: string[]; myDeals?: boolean }> }>('/dashboard/view-state');
export const saveDashboardViewState = (viewKey: string, state: object) =>
  request('/dashboard/view-state/' + encodeURIComponent(viewKey), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ state }),
  });
export const deleteDashboardViewState = (viewKey: string) =>
  request('/dashboard/view-state/' + encodeURIComponent(viewKey), { method: 'DELETE' });

// ── Today page ────────────────────────────────────────────────────────
// Auto-drafted inbound replies the user can ship in one click.
export interface InboundDraft {
  id: string;
  client: string;
  thread_id: string;
  from_addr: string;
  from_name: string;
  inbound_subject: string;
  inbound_snippet: string;
  inbound_date: string;
  draft_subject: string;
  draft_body: string;
  draft_persona: string;
  generated_at: string;
  // Reply-all defaults computed by the backend at GET time. The UI prefills
  // To/CC inputs with these; user can override before sending.
  reply_to_default: string;
  reply_cc_default: string;
  // Type-specific instruction the drainer used to write this draft. Surfaced
  // so a Discuss → Generate from the chat composer keeps the same brief
  // instead of dropping into a generic re-compose. See _generate_reply.
  brief?: string;
}
export const getAwaitingReply = (limit = 20) =>
  request<{ drafts: InboundDraft[] }>(`/inbox/awaiting?limit=${limit}`);
export const sendAwaitingReply = (
  id: string, subject?: string, body?: string, to?: string, cc?: string,
  files?: File[],
) => {
  // multipart so the caller can include uploaded custom attachments.
  // Optional text fields: omit (don't append) to mean "use stored draft",
  // pass empty string to explicitly clear (e.g. cc=""). Matches the
  // server's None vs "" sentinel.
  const fd = new FormData();
  if (subject !== undefined) fd.append('subject', subject);
  if (body !== undefined) fd.append('body', body);
  if (to !== undefined) fd.append('to', to);
  if (cc !== undefined) fd.append('cc', cc);
  if (files) for (const f of files) fd.append('files', f);
  return fetch(`${BASE}/inbox/awaiting/${encodeURIComponent(id)}/send`, {
    method: 'POST', body: fd, credentials: 'include',
  }).then(async r => {
    if (!r.ok) {
      const b = await r.json().catch(() => null);
      throw new Error(b?.detail || `${r.status} ${r.statusText}`);
    }
    return r.json();
  }) as Promise<{ ok: boolean; message_id?: string }>;
};
export const regenerateAwaitingReply = (id: string) =>
  request<{ ok: boolean; subject: string; body: string }>(
    `/inbox/awaiting/${encodeURIComponent(id)}/regenerate`,
    { method: 'POST' },
  );
export const skipAwaitingReply = (id: string) =>
  request<{ ok: boolean }>(`/inbox/awaiting/${encodeURIComponent(id)}/skip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
export const unskipAwaitingReply = (id: string) =>
  request<{ ok: boolean }>(`/inbox/awaiting/${encodeURIComponent(id)}/unskip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });

// Decisions feed — synthesized across George messages + dashboard state.
// `proactive_draft` is attached server-side for `overdue` and `going_cold`
// rows when a pending draft exists; the Today tile renders the body + Send
// in line with the decision.
export interface ProactiveDraft {
  id: string;
  subject: string;
  body: string;
  to: string;
  cc: string;
  thread_id: string;
  generated_at: string;
  attach_ids?: string[];   // populated for prospecting drafts
  // Meeting context — populated only for meeting_followup drafts, straight
  // from the synced calendar invite (no transcript). Drives the "From your
  // meeting" block that tells the rep what this reply follows from.
  meeting_subject?: string;
  meeting_start?: string;
  meeting_end?: string;
  meeting_attendees?: string[];
  meeting_team?: string[];
}
export interface Decision {
  client: string;
  type: string;
  severity: 'red' | 'amber' | 'green';
  title: string;
  context: string;
  age_days: number;
  proactive_draft?: ProactiveDraft;
  // type-specific extras (follow_up, last_contact, source_client, target, current, etc.)
  [k: string]: any;
}
export const getDecisions = () => request<{ decisions: Decision[] }>('/today/decisions');

// Prior messages of an existing thread — lazy-loaded for the overdue card's
// "View email thread" preview. Last `n` messages, bodies trimmed server-side.
export interface ThreadMessage { date: string; direction: 'Inbound' | 'Outbound'; sender: string; body: string; }
export const getThreadMessages = (threadId: string, n = 3) =>
  request<{ messages: ThreadMessage[] }>(`/threads/${encodeURIComponent(threadId)}/messages?n=${n}`);

// Aggregated payload for the Today page — one round-trip instead of 9.
// See routers/today.py:today_bundle for the backend. Each sub-result
// is either the same shape its individual endpoint returned, or null
// if that particular sub-fetch failed (the page retains its last valid
// value for that section).
export interface TodayBundle {
  decisions: { decisions: any[] } | null;
  drafts:    { drafts: any[] } | null;
  scheduled: any[] | null;
  george_messages: GeorgeMessage[] | null;
  events:    { events: any[]; auto_invite?: boolean } | null;
  dashboard: any | null;
  hunt:      HuntResponse | null;
  progress:  { goal: number; done: number; week_start: string } | null;
  outreach:  OutreachEntry[] | null;
}
export const getTodayBundle = () => request<TodayBundle>('/today/bundle');

export const dismissDecision = (
  client: string, type: string, days = 7, state_based = false,
) =>
  request<{ ok: boolean }>('/today/decisions/dismiss', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client, type, days, state_based }),
  });
export const undismissDecision = (client: string, type: string) =>
  request<{ ok: boolean }>('/today/decisions/undismiss', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client, type }),
  });
export const sendProactiveDraft = (
  id: string, subject?: string, body?: string, to?: string, cc?: string,
  attach_ids?: string[], files?: File[],
) => {
  // multipart so the caller can include uploaded custom attachments.
  // attach_ids: omit (don't append) → server uses the row's stored value;
  // pass a JSON-encoded array (including "[]") → explicit override.
  // Mirrors the SendBody None vs []  semantics from the JSON era.
  const fd = new FormData();
  if (subject !== undefined) fd.append('subject', subject);
  if (body !== undefined) fd.append('body', body);
  if (to !== undefined) fd.append('to', to);
  if (cc !== undefined) fd.append('cc', cc);
  if (attach_ids !== undefined) fd.append('attach_ids', JSON.stringify(attach_ids));
  if (files) for (const f of files) fd.append('files', f);
  return fetch(`${BASE}/today/proactive/${encodeURIComponent(id)}/send`, {
    method: 'POST', body: fd, credentials: 'include',
  }).then(async r => {
    if (!r.ok) {
      const b = await r.json().catch(() => null);
      throw new Error(b?.detail || `${r.status} ${r.statusText}`);
    }
    return r.json();
  }) as Promise<{ ok: boolean }>;
};
export const skipProactiveDraft = (id: string) =>
  request<{ ok: boolean }>(`/today/proactive/${encodeURIComponent(id)}/skip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  });
export const regenerateProactiveDraft = (id: string) =>
  request<{ id: string; subject: string; body: string; to: string; cc: string;
            attach_ids?: string[] }>(
    `/today/proactive/${encodeURIComponent(id)}/regenerate`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );

export const getProspectingProgress = () =>
  request<{ goal: number; done: number; week_start: string }>('/today/prospecting-progress');

// Today's hunt — roulette of prospecting targets + streak + scoreboard.
export interface HuntTarget {
  client: string;
  stage: string;
  slot: 'prospect' | 'tof' | 'wildcard';
  last_contact: string;
  warmth: 'warm' | 'cold';
}
export interface HuntResponse {
  targets: HuntTarget[];
  streak: { days: number; this_week: number; best_week: number };
  scoreboard: {
    months: string[];
    series: Array<{ user: string; display_name: string; counts: number[] }>;
    solo: boolean;
  };
  eligible_total: number;
}
export const getTodayHunt = () => request<HuntResponse>('/today/hunt');
export const setProspectingGoal = (goal: number) =>
  request<{ ok: boolean; goal: number }>('/today/prospecting-goal', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goal }),
  });

// Pinned clients (sidebar quick-jump)
export const getPinnedClients = () =>
  request<{ pinned: string[] }>('/dashboard/pinned');
export const pinClient = (client: string) =>
  request<{ ok: boolean; pinned: boolean }>('/dashboard/pinned/' + encodeURIComponent(client), { method: 'POST' });
export const unpinClient = (client: string) =>
  request<{ ok: boolean; pinned: boolean }>('/dashboard/pinned/' + encodeURIComponent(client), { method: 'DELETE' });

export const getStages = () => request<Record<string, string>>('/stages');
export const getStageSources = () => request<Record<string, 'manual' | 'manual_override' | 'ai'>>('/stages/sources');
export const setStage = (client: string, stage: string) =>
  request('/stages/' + encodeURIComponent(client), {
    method: 'PUT',
    body: JSON.stringify({ stage }),
  });
export const resumeAutomaticStage = (client: string) =>
  request<{ ok: boolean; stage: string; set_by: 'ai' }>('/stages/' + encodeURIComponent(client) + '/auto', {
    method: 'POST',
  });

// Threads
export const listFiles = (client: string, folder: string) =>
  request<FileEntry[]>(`/clients/${encodeURIComponent(client)}/files/${folder}`);
export const readFileContent = (client: string, folder: string, filename: string) =>
  request<{ content: string }>(`/clients/${encodeURIComponent(client)}/files/${folder}/${encodeURIComponent(filename)}`);
export const saveFileContent = (client: string, folder: string, filename: string, content: string) =>
  request<{ ok: boolean }>(`/clients/${encodeURIComponent(client)}/files/${folder}/${encodeURIComponent(filename)}`, {
    method: 'PUT', body: JSON.stringify({ content }),
  });
export const deleteFile = (client: string, folder: string, filename: string) =>
  request<{ ok: boolean }>(`/clients/${encodeURIComponent(client)}/files/${folder}/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });
export const getDocumentUrl = (client: string, filename: string) =>
  `${BASE}/clients/${encodeURIComponent(client)}/documents/${encodeURIComponent(filename)}/raw`;
export const getThreadMeta = (client: string, stem: string, threadId?: string) =>
  request<{ default_to: string; default_subject: string; gmail_thread_id: string; cc_participants?: string[]; cc_internal?: string[] }>(
    `/clients/${encodeURIComponent(client)}/thread-meta/${encodeURIComponent(stem)}${threadId ? `?tid=${encodeURIComponent(threadId)}` : ''}`
  );

// Contacts
export const getContacts = (search = '') =>
  request<Contact[]>(`/contacts?search=${encodeURIComponent(search)}`);
export const createContact = (data: Partial<Contact>) =>
  request('/contacts', { method: 'POST', body: JSON.stringify(data) });
export const updateContact = (email: string, data: Partial<Contact>) =>
  request(`/contacts/${encodeURIComponent(email)}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteContact = (email: string) =>
  request(`/contacts/${encodeURIComponent(email)}`, { method: 'DELETE' });
export const bulkCreateContacts = (contacts: Partial<Contact>[], default_tag = '') =>
  request<{ added: number; updated: number; total: number }>(
    '/contacts/bulk',
    { method: 'POST', body: JSON.stringify({ contacts, default_tag }) },
  );
export const deleteAllProspectContacts = () =>
  request<{ ok: boolean; deleted_contacts: number; deleted_clients: number; prospect_clients_total: number }>(
    '/contacts/prospects/all', { method: 'DELETE' },
  );
export const rebuildContacts = () =>
  request<{ ok: boolean; error?: string }>('/contacts/rebuild', { method: 'POST' });
export const exportContactsUrl = () => `${BASE}/contacts/export`;

// Domains
export const getDomains = () => request<Record<string, string>>('/domains');
export const setDomain = (domain: string, client: string) =>
  request(`/domains/${encodeURIComponent(domain)}`, { method: 'PUT', body: JSON.stringify({ client }) });
export const deleteDomain = (domain: string) =>
  request(`/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' });

// Client operations (promote / demote)
export const getNonClientDomains = () =>
  request<{ domains: Record<string, string[]>; personal_emails: string[] }>('/clients/non-client-domains');
export const promoteClient = (domain: string, clientName: string) =>
  request<{ ok: boolean; client: string }>('/clients/promote', {
    method: 'POST', body: JSON.stringify({ domain, client_name: clientName }),
  });
export const demoteClient = (client: string) =>
  request<{ ok: boolean; removed_domains: string[] }>('/clients/demote', {
    method: 'POST', body: JSON.stringify({ client }),
  });
export const renameClient = (oldName: string, newName: string) =>
  request<{ ok: boolean; old_name: string; new_name: string; gcs_blobs_moved: number }>(
    '/clients/rename',
    { method: 'POST', body: JSON.stringify({ old_name: oldName, new_name: newName }) },
  );

// Email
export const generateEmail = (data: {
  client: string; instruction?: string; current_to?: string;
  current_subject?: string; thread_id?: string; context_md?: string; persona?: string;
}) => request<{ to: string; cc: string; subject: string; body: string }>('/email/generate', {
  method: 'POST', body: JSON.stringify(data),
});
export const saveDraft = (data: {
  to: string; subject: string; body: string; cc?: string; thread_id?: string;
  client?: string; outreach_doc_ids?: string[]; files?: File[];
}) => {
  const fd = new FormData();
  fd.append('to', data.to);
  fd.append('subject', data.subject);
  fd.append('body', data.body);
  fd.append('cc', data.cc || '');
  fd.append('thread_id', data.thread_id || '');
  fd.append('client', data.client || '');
  if (data.outreach_doc_ids?.length) {
    fd.append('outreach_doc_ids', JSON.stringify(data.outreach_doc_ids));
  }
  if (data.files) {
    for (const f of data.files) fd.append('files', f);
  }
  return fetch(`${BASE}/email/save-draft`, { method: 'POST', body: fd, credentials: 'include' })
    .then(async r => { if (!r.ok) { const b = await r.json().catch(() => null); throw new Error(b?.detail || `${r.status} ${r.statusText}`); } return r.json(); }) as Promise<{ draft_id: string }>;
};

// Polish
export const polishEmail = (data: { client: string; body: string; subject?: string; mode?: 'polish' | 'refine' }) =>
  request<{ subject: string; body: string; changes: string }>('/email/polish', {
    method: 'POST', body: JSON.stringify(data),
  });

// Chat
export const chat = (data: { client: string; messages: { role: string; content: string }[]; ref_thread?: string; mode?: string }) =>
  // Backend returns { reply, tool_calls } — the web-search/client-lookup tool
  // variant populates tool_calls (chat.py); the plain path returns []. Optional
  // so callers can `r.tool_calls?.length`.
  request<{ reply: string; tool_calls?: any[] }>('/chat', { method: 'POST', body: JSON.stringify(data) });
export const saveInsights = (data: { client: string; messages: { role: string; content: string }[]; update_file?: string }) =>
  request<{ saved_to: string; summary: string }>('/chat/save-insights', { method: 'POST', body: JSON.stringify(data) });

// Knowledge
export interface KnowledgeEntry {
  id: string;
  title: string;
  when_to_use: string;
  source: string;
  detail_file: string;
  access: string;
  date: string;
  detail: string;
}
export const getKnowledgeIndex = () => request<KnowledgeEntry[]>('/knowledge/index');
export const processKnowledgeFile = async (file: File, whenToUse: string) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('when_to_use', whenToUse);
  const r = await fetch(`${BASE}/knowledge/process`, { method: 'POST', body: fd, credentials: 'include' });
  if (!r.ok) {
    // Surface FastAPI's HTTPException `detail` so 413 (token-limit) and other
    // structured errors carry their full message to the toast, not just the status code.
    let detail = '';
    try {
      const body = await r.json();
      if (body && typeof body.detail === 'string') detail = body.detail;
    } catch { /* non-JSON body — fall through */ }
    throw new Error(detail || `${r.status} ${r.statusText}`);
  }
  return await r.json() as {
    title: string; summary: string; when_to_use: string; filename: string;
  };
};
export const saveKnowledgeEntry = (data: {
  title: string; summary: string; when_to_use: string; source: string; access: string;
}) => request<{ entry_id: string; title: string }>('/knowledge/save', { method: 'POST', body: JSON.stringify(data) });
export const toggleKnowledgeAccess = (entryId: string, access: string) =>
  request('/knowledge/toggle-access', { method: 'POST', body: JSON.stringify({ entry_id: entryId, access }) });
export const deleteKnowledgeEntry = (entryId: string) =>
  request(`/knowledge/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
export const updateKnowledgeEntry = (entryId: string, data: {
  title?: string; when_to_use?: string; summary?: string;
}) => request(`/knowledge/${encodeURIComponent(entryId)}`, { method: 'PUT', body: JSON.stringify(data) });

// Outreach
export interface OutreachEntry {
  id: string;
  title: string;
  when_to_attach: string;
  original_filename: string;
  stored_filename: string;
  detail_file: string;
  date: string;
  detail: string;
}
export const getOutreachIndex = () => request<OutreachEntry[]>('/outreach/index');
export const processOutreachFile = (file: File, whenToAttach: string) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('when_to_attach', whenToAttach);
  return fetch(`${BASE}/outreach/process`, { method: 'POST', body: fd, credentials: 'include' })
    .then(r => { if (!r.ok) throw new Error(`${r.status} ${r.statusText}`); return r.json(); }) as Promise<{
    title: string; summary: string; when_to_attach: string; filename: string;
  }>;
};
export const saveOutreachEntry = (file: File, data: {
  title: string; summary: string; when_to_attach: string;
}) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('title', data.title);
  fd.append('summary', data.summary);
  fd.append('when_to_attach', data.when_to_attach);
  return fetch(`${BASE}/outreach/save`, { method: 'POST', body: fd, credentials: 'include' })
    .then(r => { if (!r.ok) throw new Error(`${r.status} ${r.statusText}`); return r.json(); }) as Promise<{ entry_id: string; title: string }>;
};
export const updateOutreachEntry = (entryId: string, data: { when_to_attach?: string; summary?: string }) =>
  request(`/outreach/${encodeURIComponent(entryId)}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteOutreachEntry = (entryId: string) =>
  request(`/outreach/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
export const getOutreachRecommendations = (client: string) =>
  request<OutreachEntry[]>(`/outreach/recommend/${encodeURIComponent(client)}`);
export const markOutreachSent = (client: string, docIds: string[]) =>
  request('/outreach/mark-sent', { method: 'POST', body: JSON.stringify({ client, doc_ids: docIds }) });
export const getOutreachSent = (client: string) =>
  request<string[]>(`/outreach/sent/${encodeURIComponent(client)}`);

// Prospecting
export interface HistoryEntry {
  id: string;
  client: string;
  subject: string;
  sender: string;
  got_reply: boolean;
  date: string;
  detail_file: string;
  detail: string;
}
export const getProspectingHistory = () => request<HistoryEntry[]>('/prospecting/history');
export const scanProspectingHistory = () =>
  request<{ added: number; total: number }>('/prospecting/scan', { method: 'POST' });
export const cleanupProspectingHistory = () =>
  request<{ removed: number; remaining: number }>('/prospecting/cleanup', { method: 'POST' });
export const deleteHistoryEntry = (entryId: string) =>
  request(`/prospecting/history/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
export const generateColdOutreach = (data: {
  client: string; contact_email: string; contact_name?: string; instruction?: string; persona?: string;
  use_conversion_examples?: boolean;
}) => request<{
  to: string; subject: string; body: string;
  recommended_attachments: string[]; research_summary: string;
}>('/prospecting/generate', { method: 'POST', body: JSON.stringify(data) });
export const researchProspect = (data: { client: string; contact_email: string }) =>
  request<{ research: string }>('/prospecting/research', { method: 'POST', body: JSON.stringify(data) });

// Settings / Personas
export interface Persona {
  name: string;
  label: string;
  background: string;
  style: string;
  special?: string;
}
export interface PersonasData {
  active: string;
  personas: Record<string, Persona>;
}
export interface BusinessConfig {
  company_name: string;
  product_name: string;
  product_description: string;
  industry: string;
  industry_search_terms: string;
  news_search_terms: string;
  target_audience: string;
  outreach_objective?: 'call' | 'self_serve';
  self_serve_url?: string;
  ai_assistant_name: string;
  ai_assistant_expertise: string;
  deep_read_chat: boolean;
  deep_read_calls: boolean;
  // ⚠️ ASYMMETRIC: you WRITE the string 'true'/'false', you READ BACK a boolean.
  // `set_business_config` stores a str verbatim; `get_business_config` runs
  // json.loads over every value, so "true" comes back as JSON `true`. Verified
  // by execution on a *.test tenant, not by reading. Hence the union — typing
  // this `string` alone made `cfg.personal_emails_as_clients === 'true'` look
  // safe when it is silently and permanently false. Compare with
  // `String(v).toLowerCase() === 'true'`, which the poller does too
  // (_pipeline/gmail_poller.py:1180) and which handles both forms.
  personal_emails_as_clients?: string | boolean;
}
// ── In-flight de-duplication, for these two endpoints ONLY ──────────────────
// `App.svelte` fetches /settings/business + /settings/personas on EVERY page to
// resolve the agent name for the sidebar; Settings and Teach George then fetch
// the same two in their own onMount. On a hard load of /teach that measured as
// **/settings/business ×2 and /settings/personas ×2** — ~5.4 KB re-fetched, and
// /settings/business is the endpoint that returns the whole 85-key blob.
//
// This is **not a cache.** The entry is dropped the moment the promise settles,
// so anything that starts later still re-fetches and nothing can go stale — it
// only collapses calls that genuinely overlap in time. Clicking through the
// sidebar is unaffected (measured: it never duplicated in the first place).
//
// ⚠️ Deliberately NOT applied inside `request()`. Some GET routes in this API
// have side effects (`GET /dashboard/followup/{client}` writes — see the
// UPDATES landmine), and collapsing two concurrent side-effectful GETs into one
// would be a behaviour change, not an optimisation. Opt in per endpoint.
const _inFlight = new Map<string, Promise<unknown>>();
function dedupe<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = _inFlight.get(key) as Promise<T> | undefined;
  // Each caller gets its own copy: sharing one object instance across two
  // components invites an in-place mutation in one to surface in the other.
  if (existing) return existing.then(v => structuredClone(v));
  const p = run().finally(() => _inFlight.delete(key));
  _inFlight.set(key, p);
  return p;
}
// Writes drop the pending read. Without this there is a narrow but real window:
// a GET already in flight when a PUT lands would still be joined by the
// re-read that every save does (`await update…(); biz = await get…()`), so the
// page would render PRE-save data and look like the save silently failed.
// Dropping the key doesn't cancel that request — it just guarantees the next
// GET starts its own.
function _afterWrite<T>(key: string, p: Promise<T>): Promise<T> {
  _inFlight.delete(key);
  return p.finally(() => _inFlight.delete(key));
}

export const getBusinessConfig = () =>
  dedupe('business', () => request<BusinessConfig>('/settings/business'));
// PUT is a per-key upsert (state_db.set_business_config), not a replace — omitted
// keys are left alone. Send only the keys you own: Settings owns
// `personal_emails_as_clients`, Teach George the profile. A whole-snapshot PUT from
// either page would clobber the other's edits. (Verified on a *.test tenant.)
export const updateBusinessConfig = (data: Partial<BusinessConfig>) =>
  _afterWrite('business', request('/settings/business', { method: 'PUT', body: JSON.stringify(data) }));

export const getPersonas = () =>
  dedupe('personas', () => request<PersonasData>('/settings/personas'));
export const setActivePersona = (active: string) =>
  _afterWrite('personas', request('/settings/personas/active', { method: 'PUT', body: JSON.stringify({ active }) }));
export const updatePersona = (key: string, data: { name?: string; background?: string; style?: string }) =>
  _afterWrite('personas', request(`/settings/personas/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(data) }));

// Email tracking
export const getDashboardTracking = () => request<{
  stats: Record<string, { total_opens: number; max_opens: number }>;
  recent: Record<string, Array<{ tracking_id: string; recipient: string; subject: string; sent_at: string; open_count: number; first_opened: string; last_opened: string; open_events: string }>>;
}>('/dashboard/tracking');

// Local cache
export const getLocalCache = () => request<{ enabled: boolean }>('/settings/local-cache');
export const setLocalCache = (enabled: boolean) =>
  request<{ ok: boolean; enabled: boolean }>('/settings/local-cache', { method: 'PUT', body: JSON.stringify({ enabled }) });

// Email dispatch preferences (platform-sent emails — default ON per type)
export interface EmailPrefs { george_messages: boolean; pre_call_briefs: boolean; }
export const getEmailPrefs = () => request<EmailPrefs>('/settings/email-prefs');
export const setEmailPrefs = (prefs: Partial<EmailPrefs>) =>
  request<{ ok: boolean; prefs: EmailPrefs }>('/settings/email-prefs', { method: 'PUT', body: JSON.stringify(prefs) });

// Poller
export const startPoll = (fullRebuild = false) => request<{ status: string; pid?: number }>(`/poller/start${fullRebuild ? '?full_rebuild=true' : ''}`, { method: 'POST' });
export const wipeAndRebuild = () => request<{ status: string }>('/poller/wipe', { method: 'POST' });
export const pollStatus = () => request<{ running: boolean; exit_code: number | null; log_tail: string; progress: { phase: string; total: number; processed: number; clients: number; personal_excluded?: number; automated_skipped?: number } | null }>('/poller/status');
export const rebuildClient = (client: string, opts: { emails?: boolean; strategy?: boolean; contacts?: boolean } = {}) =>
  request<{ status: string; client: string; wiped?: string[]; pid?: number }>(`/poller/rebuild/${encodeURIComponent(client)}`, {
    method: 'POST', body: JSON.stringify({ emails: true, ...opts }),
  });
export const rebuildClientStatus = (client: string) =>
  request<{ running: boolean; exit_code: number | null; log_tail: string }>(`/poller/rebuild/${encodeURIComponent(client)}/status`);

// Lead Discovery
export interface DiscoveredLead {
  company_name: string;
  domain: string;
  description: string;
  key_contacts: { name: string; title: string; linkedin_url: string }[];
  relevance_score: number;
  reasoning: string;
}
export const discoverLeads = (query: string, maxResults = 10) =>
  request<DiscoveredLead[]>('/leads/discover', {
    method: 'POST',
    body: JSON.stringify({ query, max_results: maxResults }),
  });
export const saveLead = (data: {
  company_name: string; domain?: string; description?: string;
  contact_name?: string; contact_email?: string; contact_title?: string;
}) =>
  request<{ ok: boolean; client: string; error?: string }>('/leads/save', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Sequences
export interface SequenceEmail {
  step: number;
  subject: string;
  body: string;
  delay_days: number;
  status: 'pending' | 'sent' | 'skipped';
  sent_at: string | null;
  draft_id: string | null;
  recommended_attachments?: string[];   // doc ids the generator suggested (prospecting.py)
}
export interface Sequence {
  client: string;
  contact_email: string;
  contact_name: string;
  brief: string;
  created_at: string;
  active: boolean;
  emails: SequenceEmail[];
  sent?: number;
  pending?: number;
  skipped?: number;
  research?: string;   // research context merged onto the sequence detail payload
}
export interface ConversionEmail {
  client: string;
  user_email: string;
  outbound_date: string;
  subject: string;
  body_snippet: string;
  inbound_date: string;
  outbound_sequence: string;
  inbound_response: string;
  score: number;
  /** Pinned by the user — forced into the set whoever owns it. */
  selected: boolean;
  /** Computed server-side: is this one of the emails the generator actually gets?
   *  Never re-derive this in the UI; a second copy of the rule can disagree with it. */
  learned: boolean;
}
export const getConversions = () => request<ConversionEmail[]>('/sequences/conversions');
/** Pin/unpin this email for the generator. All 5 slots may be pinned; a 6th rejects with 409. */
export const toggleConversionStar = (client: string) =>
  request<{ starred: boolean; count: number; max: number }>(
    `/sequences/conversions/${encodeURIComponent(client)}/star`, { method: 'POST' });
export const getConversionSettings = () => request<{ team_mode: boolean }>('/sequences/conversions/settings');
export const toggleConversionTeamMode = () =>
  request<{ team_mode: boolean }>('/sequences/conversions/settings', { method: 'POST' });
export const getSequences = () => request<Sequence[]>('/sequences');
export const getSequence = (client: string) =>
  request<Sequence>(`/sequences/${encodeURIComponent(client)}`);
export const createSequence = (data: {
  client: string; contact_email: string; contact_name?: string;
  brief: string; persona?: string; voice?: string; num_emails?: number;
  delay_2?: number; delay_3?: number; marketing_ids?: string[];
}) =>
  request<Sequence>('/sequences', { method: 'POST', body: JSON.stringify(data) });
export const updateSequence = (client: string, emails: Partial<SequenceEmail>[]) =>
  request<{ ok: boolean }>(`/sequences/${encodeURIComponent(client)}`, {
    method: 'PUT', body: JSON.stringify({ emails }),
  });
export const activateSequence = (client: string) =>
  request<{ ok: boolean }>(`/sequences/${encodeURIComponent(client)}/activate`, { method: 'POST' });
export const deactivateSequence = (client: string) =>
  request<{ ok: boolean }>(`/sequences/${encodeURIComponent(client)}/deactivate`, { method: 'POST' });
export const deleteSequence = (client: string) =>
  request<{ ok: boolean }>(`/sequences/${encodeURIComponent(client)}`, { method: 'DELETE' });

// George (Mr. GAUGE) — Live Call Agent
export type GeorgeMode = 'prompted' | 'conversational' | 'listener';   // 'listener' = joins + transcribes only, no speech (george.py:57)
export type GeorgeSpeakingState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'paused';

export interface GeorgeSettings {
  mode: GeorgeMode;
  activated: boolean;
  voice_id?: string;
  endpointing_ms?: number;
}

export interface GeorgeState {
  mode: GeorgeMode;
  activated: boolean;
  in_call: boolean;
  client: string;
  call_start: string | null;
  speaking_state: GeorgeSpeakingState;
  paused: boolean;
  transcript_length: number;
  agent_connected: boolean;
  agent_id?: string;   // present on multi-agent WS state payloads (used to filter to our agent)
}

export interface TranscriptLine {
  ts: string;
  speaker: string;
  text: string;
  is_george: boolean;
}

export interface GeorgeBotAccount {
  bot_email: string;
  bot_display_name: string;
  custom_account: boolean;
  session_saved: boolean;
}

export const getGeorgeSettings = () => request<GeorgeSettings>('/george/settings');
export const updateGeorgeSettings = (data: Partial<GeorgeSettings>) =>
  request('/george/settings', { method: 'PUT', body: JSON.stringify(data) });
export const getGeorgeState = () => request<GeorgeState>('/george/state');
export const getGeorgeTranscript = (offset = 0, limit = 100) =>
  request<{ lines: TranscriptLine[]; total: number }>(`/george/transcript?offset=${offset}&limit=${limit}`);

// George bot account
export const getGeorgeBotAccount = () => request<GeorgeBotAccount>('/george/bot-account');
export const updateGeorgeBotAccount = (data: Partial<GeorgeBotAccount>) =>
  request<GeorgeBotAccount & { ok: boolean }>('/george/bot-account', { method: 'PUT', body: JSON.stringify(data) });
export const resetGeorgeBotSession = () =>
  request<{ ok: boolean }>('/george/bot-account/reset-session', { method: 'POST' });

// George speech config
export interface GeorgeSpeechConfig {
  abbreviations: Record<string, string>;
  // Backend normalizes every entry to {replacement, whole_word} (george.py:388).
  pronunciations: Record<string, { replacement: string; whole_word: boolean }>;
}
export const getGeorgeSpeechConfig = () => request<GeorgeSpeechConfig>('/george/speech-config');
export const updateGeorgeSpeechConfig = (config: { abbreviations?: Record<string, string>; pronunciations?: Record<string, { replacement: string; whole_word: boolean }> }) =>
  request<GeorgeSpeechConfig>('/george/speech-config', { method: 'PUT', body: JSON.stringify(config) });

// George strategy call
export const launchStrategyCall = (client: string, callMode: 'strategy' | 'client' = 'strategy') =>
  request<{ ok: boolean; meeting_url?: string; client?: string; error?: string }>(
    '/george/strategy-call', { method: 'POST', body: JSON.stringify({ client, call_mode: callMode }) }
  );

// George controls — target a specific agent if agent_id provided
export const georgeControl = (action: 'pause' | 'continue' | 'start' | 'stop' | 'activate' | 'deactivate' | 'rejoin' | 'leave', agentId = '') =>
  request(`/george/control/${action}${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`, { method: 'POST' });

// George active agents
export interface ActiveAgent {
  agent_id: string;
  mode: GeorgeMode;
  activated: boolean;
  in_call: boolean;
  client: string;
  meeting_url: string;
  bot_email: string;
  call_start: string | null;
  speaking_state: GeorgeSpeakingState;
  paused: boolean;
  transcript_length: number;
  agent_connected: boolean;
  created_at: string;
}
export const getActiveAgents = () => request<ActiveAgent[]>('/george/agents');
export const getScheduledCalls = () => request<any[]>('/george/scheduled');
export const syncScheduledCalls = () =>
  request<{ checked: number; updated: number; cancelled: number; error?: string }>('/george/calls/sync', { method: 'POST' });

// George call history
export interface CallEntry {
  client: string;
  type: 'meeting' | 'strategy';
  filename: string;
  stem: string;
  date: string;
  preview: string;
  has_transcript: boolean;
  transcript_filename: string | null;
}
export const getCallHistory = (client = '') =>
  request<CallEntry[]>(`/george/calls${client ? `?client=${encodeURIComponent(client)}` : ''}`);
export const getClientCalls = (client: string) =>
  request<CallEntry[]>(`/george/calls?client=${encodeURIComponent(client)}`);
export const readCall = (client: string, callType: string, filename: string) =>
  request<{ content: string; filename: string }>(`/george/calls/${encodeURIComponent(client)}/${callType}/${encodeURIComponent(filename)}`);
export const readTranscript = (client: string, callType: string, filename: string) =>
  request<{ content: string; filename: string }>(`/george/calls/${encodeURIComponent(client)}/${callType}/transcripts/${encodeURIComponent(filename)}`);
export const deleteCall = (client: string, callType: string, filename: string) =>
  request<{ ok: boolean; deleted: string[] }>(`/george/calls/${encodeURIComponent(client)}/${callType}/${encodeURIComponent(filename)}`, { method: 'DELETE' });

// George WebSocket — returns a WebSocket connection for real-time updates.
// BASE is now relative ('/api'), so the old `.replace('https://','wss://')` was
// a no-op — `new WebSocket('/api/...')` only worked because browsers resolve a
// relative WS URL against the page origin. Make that explicit instead of relying
// on it: build an absolute ws(s):// URL from the current origin (wss on https,
// ws on http — which covers prod nginx and the vite dev proxy alike). Still
// handles a legacy absolute BASE if one is ever reintroduced.
const WS_BASE = /^https?:\/\//.test(BASE)
  ? BASE.replace(/^http/, 'ws')
  : `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${BASE}`;
export function connectGeorgeWS(handlers: {
  onState?: (data: GeorgeState) => void;
  onTranscript?: (line: TranscriptLine) => void;
  onTranscriptHistory?: (lines: TranscriptLine[]) => void;
  onSpeaking?: (data: { state: GeorgeSpeakingState }) => void;
  onSettings?: (data: GeorgeSettings) => void;
  onClose?: () => void;
}): WebSocket {
  const ws = new WebSocket(`${WS_BASE}/george/ws/dashboard`);

  ws.onmessage = (event) => {
    const msg = JSON.parse(event.data);
    switch (msg.type) {
      case 'state':
        handlers.onState?.(msg.data);
        break;
      case 'transcript':
        handlers.onTranscript?.(msg.data);
        break;
      case 'transcript_history':
        handlers.onTranscriptHistory?.(msg.data);
        break;
      case 'speaking':
        handlers.onSpeaking?.(msg.data);
        break;
      case 'settings':
        handlers.onSettings?.(msg.data);
        break;
    }
  };

  ws.onclose = () => handlers.onClose?.();

  return ws;
}

// Onboarding
/** The boot call. It also carries the workflow vocabulary, so no page can paint
 *  before the app knows whether to say "Client" or "Committed LP" — see the
 *  `terms` store in lib/stores.ts. */
export const getOnboardingStatus = () =>
  request<{
    completed: boolean; company_setup: boolean; user_setup: boolean; user_name: string;
    workflow: 'sales' | 'fund'; account_type_locked: boolean;
    vocabulary: {
      workflow: 'sales' | 'fund';
      stage_labels: Record<string, string>;
      terms: Record<string, string>;
    };
  }>('/onboarding/status');

export const generateBackstory = (data: {
  mode: 'dark' | 'light';
  persona_name: string;
  expert_type: string;
  company_name: string;
  product_name: string;
  product_description: string;
  target_audience: string;
  industry: string;
  // Picks the label the persona prompt uses for what the org offers:
  // 'Product' for Sales, 'Fund' for a fund account.
  user_type?: 'corporate' | 'fund';
}) => request<{ backstory: string; mode: string }>('/onboarding/generate-backstory', {
  method: 'POST', body: JSON.stringify(data),
});

export const completeOnboarding = (data: {
  user_name: string;
  user_background?: string;
  user_writing_style?: string;
  company_name: string;
  product_name: string;
  product_description: string;
  industry: string;
  target_audience: string;
  expert_types: string[];
  persona_name: string;
  persona_backstory: string;
  persona_backstory_mode: string;
  ai_assistant_expertise?: string;
  outreach_objective?: 'call' | 'self_serve';
  self_serve_url?: string;
  personal_emails_as_clients?: string;   // 'true'/'false' string; round-trips via the generic config blob
  // 'fund' makes the backend record this user in `fund_users`, which is what
  // the user type IS — there is no discriminator column.
  user_type?: 'corporate' | 'fund';
  // Sent structured; the backend also joins them into industry/target_audience
  // so the existing consumers of those two keep working.
  sector_focus?: string[];
  target_lp_types?: string[];
  funds?: { name: string; strategy: string }[];
}) => request<{ ok: boolean }>('/onboarding/complete', {
  method: 'POST', body: JSON.stringify(data),
});

export const completeUserOnboarding = (data: { user_name: string; user_background?: string; user_writing_style?: string }) =>
  request<{ ok: boolean }>('/onboarding/complete-user', {
    method: 'POST', body: JSON.stringify(data),
  });

export const resetOnboarding = () =>
  request<{ ok: boolean; backup_saved?: boolean }>('/onboarding/reset', { method: 'POST' });

export const restoreOnboarding = () =>
  request<{ ok: boolean }>('/onboarding/restore', { method: 'POST' });

// Types
export interface DashboardRow {
  client: string;
  last_contact: string;
  follow_up: string;
  summary: string;
  score?: number;             // composite/total lead score (-1 = unscored sentinel)
  ai_score?: number;          // AI deal score; == total on most rows, 0 when unscored
  score_components?: Record<string, number>;
  ai_reasoning?: string;
  signals_count?: number;
  // Most-recent inbound / outbound dates per client (max across all the
  // client's threads). Used by the Pipeline's Inbound + Outbound columns.
  // Empty string when the client has no message of that direction.
  last_inbound?: string;
  last_outbound?: string;
}

// Dashboard signals & scores
export interface IntentSignal {
  title: string;
  url: string;
  snippet: string;
  date: string;
  source: string;
}
export const getIntentSignals = () => request<{
  built_at: string;
  signals: Record<string, { items: IntentSignal[]; last_checked: string }>;
}>('/dashboard/signals');
export const getLeadScores = () => request<{
  built_at: string;
  scores: Record<string, { rule_score: number; ai_score: number; total: number; ai_reasoning: string; components: Record<string, number> }>;
}>('/dashboard/scores');

export interface ScoreHistoryEntry {
  date: string;
  score: number;
  reasoning: string;
}
export interface TimelineEvent {
  date: string;
  type: string;  // "Meeting" | "Call"
  name: string;
}
export const getScoreHistory = (client: string) => request<{
  client: string;
  history: ScoreHistoryEntry[];
  events: TimelineEvent[];
}>(`/dashboard/score-history/${encodeURIComponent(client)}`);

// Follow-up overrides
export const getFollowup = (client: string) => request<{
  client: string;
  stage: string;
  follow_up: string;
  follow_up_reason: string;
  override: { date: string; reason?: string; set_by: string } | null;
}>(`/dashboard/followup/${encodeURIComponent(client)}`);
export const setFollowup = (client: string, date: string, reason = '') =>
  request(`/dashboard/followup/${encodeURIComponent(client)}`, {
    method: 'PUT',
    body: JSON.stringify({ date, reason }),
  });
export const clearFollowup = (client: string) =>
  request<{ ok: boolean; follow_up: string }>(`/dashboard/followup/${encodeURIComponent(client)}`, { method: 'DELETE' });

export interface FileEntry {
  name: string;
  stem: string;
  clean_name?: string;
  size_kb?: number;
  ext?: string;
  icon?: string;
  // Thread-folder entries also carry these; documents/strategy do not.
  thread_id?: string;              // user-mapped Gmail thread id
  canonical_thread_id?: string;    // canonical (DB-key) thread id
  last_date?: string;
  summary?: string;
  has_inbound?: boolean;
  follow_up?: string;
  subject?: string;                // real Subject header; may be '' on older rows
}

export interface Contact {
  client: string;
  name: string;
  email: string;
  title: string;
  phone: string;
  org: string;
  last_seen: string;
  thread_count: number;
  linkedin: string;
  notes: string;
  manual: boolean;
  tag?: string;
}
